// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React, { useState } from 'react';
import { DataTable, DataTableExpandedRows } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { toast } from 'react-toastify';
import { WorkingHoursSideBarFilter } from './filters/working-hours-side-bar-filter';
import { getWorkingHoursOperations } from './operations/get-working-hours-operations';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';
import {
  WorkingHoursEntity2,
  WorkingHoursResponse,
  WorkingHoursStatus,
} from '../../../../../api/types/lecturers/working-hours/working-hours.type';
import dateFormat from '../../../../../utils/date-format';
import { WorkingHoursHistory } from './componenets/working-hours-history';
import { WorkingHoursStatusTemplate } from './componenets/status-template/Working-hours-status-template';
import { WorkingHoursLoadingTemplate } from './componenets/loading/working-hours-loading-template';
import { AddManualWorkingHoursDialog } from './componenets/add-manual-working-hours-dialog/add-manual-working-hours-dialog';
import { updateWorkingHoursStatusOperation } from './operations/update-working-hours-status-operation';

export interface WorkingHoursDatamodel {
  user: UserSummaryType;
}

export const WorkingHours: React.FC<WorkingHoursDatamodel> = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WorkingHoursResponse[]>([]);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editData, setEditData] = useState<WorkingHoursResponse | null>(null);

  const handleSearch = async (startDate: Date, endDate: Date) => {
    await getWorkingHoursOperations(
      user.id,
      startDate,
      endDate,
      setLoading,
      setData,
    );
  };

  const handleEdit = (rowData: WorkingHoursResponse) => {
    setEditData(rowData);
    setShowAddDialog(true);
  };

  const handleOpenAddDialog = () => {
    setEditData(null);
    setShowAddDialog(true);
  };

  const handleSetShowAddDialog = (show: boolean) => {
    setShowAddDialog(show);
    if (!show) {
      setEditData(null);
    }
  };

  const handleApprove = async (historyItem: WorkingHoursEntity2) => {
    toast.success(
      `אושר פריט מתאריך ${dateFormat.displayFormatDate(historyItem.startAt)}`,
    );
    await updateWorkingHoursStatusOperation(
      historyItem.id,
      WorkingHoursStatus.Approved,
    );
  };

  const handleReject = async (historyItem: WorkingHoursEntity2) => {
    await updateWorkingHoursStatusOperation(
      historyItem.id,
      WorkingHoursStatus.Reject,
    );
  };

  // Column templates for main table
  const startDateTemplate = (rowData: WorkingHoursResponse) => {
    return dateFormat.displayFormatDate(rowData.startAt);
  };

  const endDateTemplate = (rowData: WorkingHoursResponse) => {
    return dateFormat.displayFormatDate(rowData.endAt);
  };

  const activityTypeTemplate = (rowData: WorkingHoursResponse) => {
    return `סוג פעילות ${rowData.activityTypeName}`;
  };

  const isManualTemplate = (rowData: WorkingHoursResponse) => {
    return rowData.isManual ? 'ידני' : 'אוטומטי';
  };

  const rateTemplate = (rowData: WorkingHoursResponse) => {
    return `₪${rowData.branchLecturersRates}`;
  };

  const editTemplate = (rowData: WorkingHoursResponse) => {
    return (
      <div
        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
      >
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-text p-button-sm"
          onClick={(e) => {
            e.stopPropagation();
            handleEdit(rowData);
          }}
          tooltip="עריכה"
        />

        <Button
          icon="pi pi-times"
          severity="warning"
          text
          size="small"
          tooltip="ביטול"
          tooltipOptions={{ position: 'top' }}
          style={{ height: '24px', width: '24px' }}
          onClick={(e) => {
            e.stopPropagation();
            handleReject(rowData);
          }}
        />
        <Button
          icon="pi pi-check"
          severity="success"
          text
          size="small"
          tooltip="אישור"
          tooltipOptions={{ position: 'top' }}
          style={{ height: '24px', width: '24px' }}
          onClick={(e) => {
            e.stopPropagation();
            handleApprove(rowData);
          }}
        />
      </div>
    );
  };

  // Expanded row template for history
  const expandedRowTemplate = (data: WorkingHoursResponse) => (
    <WorkingHoursHistory
      data={data}
      handleApprove={handleApprove}
      handleReject={handleReject}
    />
  );

  const expanderTemplate = (rowData: WorkingHoursResponse) => {
    const isExpanded = expandedRows[rowData.id] !== undefined;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          const newExpandedRows = { ...expandedRows };
          if (isExpanded) {
            delete newExpandedRows[rowData.id];
          } else {
            newExpandedRows[rowData.id] = true;
          }
          setExpandedRows(newExpandedRows);
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
        }}
      >
        {isExpanded ? '▼' : '▶'}
      </button>
    );
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        gap: '16px',
      }}
    >
      {/* Sidebar Filter */}
      <div style={{ width: '300px', flexShrink: 0 }}>
        <WorkingHoursSideBarFilter onSearch={handleSearch} />
      </div>

      {/* Main Content Area */}
      <div
        style={{
          flex: 1,
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header with Add Button */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 16px 24px',
            borderBottom: '1px solid #e9ecef',
          }}
        >
          <h2 style={{ margin: 0, color: '#333' }}>שעות עבודה</h2>
          <Button
            icon="pi pi-plus"
            label="הוסף שעות עבודה"
            className="p-button-success"
            onClick={handleOpenAddDialog}
          />
        </div>

        <div style={{ padding: '24px' }}>
          {loading ? (
            <WorkingHoursLoadingTemplate />
          ) : data.length === 0 ? (
            <p style={{ color: '#666', fontSize: '16px', textAlign: 'right' }}>
              בחר טווח תאריכים מהסינון כדי לצפות בשעות העבודה שלך
            </p>
          ) : (
            <DataTable
              value={data}
              expandedRows={expandedRows}
              onRowToggle={(e) =>
                setExpandedRows(e.data as DataTableExpandedRows)
              }
              rowExpansionTemplate={expandedRowTemplate}
              dataKey="id"
              showGridlines
              tableStyle={{ direction: 'rtl', width: '100%' }}
              emptyMessage={
                <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
              }
            >
              <Column
                expander={false}
                body={expanderTemplate}
                style={{ width: '3rem', textAlign: 'center' }}
              />
              <Column
                field="startAt"
                header="תאריך התחלה"
                body={startDateTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="startAt"
                header="שעת התחלה"
                body={(rowData: WorkingHoursResponse) => (
                  <label>
                    {dateFormat.formatTimeFromDate(rowData.startAt)}
                  </label>
                )}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="endAt"
                header="שעת סיום"
                body={(rowData: WorkingHoursResponse) => (
                  <label>{dateFormat.formatTimeFromDate(rowData.endAt)}</label>
                )}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="status"
                header="סטטוס"
                body={WorkingHoursStatusTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="activityTypeName"
                header="סוג פעילות"
                body={activityTypeTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="isManual"
                header="סוג"
                body={isManualTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                field="branchLecturersRates"
                header="תעריף"
                body={rateTemplate}
                sortable
                style={{ textAlign: 'right' }}
              />
              <Column
                header="פעולות"
                body={editTemplate}
                style={{ textAlign: 'center', width: '80px' }}
              />
            </DataTable>
          )}
        </div>
      </div>

      <AddManualWorkingHoursDialog
        userId={user.id}
        showAddDialog={showAddDialog}
        setShowAddDialog={handleSetShowAddDialog}
        editData={editData}
      />
    </div>
  );
};
